import React from 'react';
import { Shield, Lock, Eye, Trash2, Download, Bell, MapPin, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const Section = ({ number, title, children }) => (
  <section className="mb-xxxl">
    <div className="flex items-start gap-md mb-lg">
      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary text-caption font-bold flex items-center justify-center mt-xs">
        {number}
      </span>
      <h2 className="text-title-2 font-rounded text-primary">{title}</h2>
    </div>
    <div className="ml-[48px] space-y-md text-body text-text-secondary leading-relaxed">
      {children}
    </div>
  </section>
);

const SubSection = ({ title, children }) => (
  <div className="mb-lg">
    <h3 className="text-callout font-semibold text-text-primary mb-sm">{title}</h3>
    {children}
  </div>
);

const BulletList = ({ items }) => (
  <ul className="space-y-sm mt-sm">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-sm">
        <ChevronRight size={14} className="text-primary flex-shrink-0 mt-1" />
        <span className="text-body text-text-secondary">{item}</span>
      </li>
    ))}
  </ul>
);

const PrivacyPolicyView = () => {
  return (
    <div className="max-w-4xl mx-auto">

      {/* Header */}
      <div className="mb-xxxl">
        <div className="flex items-center gap-md mb-lg">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Shield size={24} className="text-primary" />
          </div>
          <div>
            <h1 className="text-display font-rounded text-primary">Privacy Policy</h1>
            <p className="text-caption text-text-tertiary">Last Updated: January 12, 2026</p>
          </div>
        </div>
        <p className="text-body text-text-secondary mb-lg leading-relaxed">
          Your privacy matters. Here's how we protect and handle your data.
        </p>

        {/* Short version callout */}
        <div
          className="p-lg rounded-large"
          style={{ background: 'rgba(var(--color-primary),0.05)', border: '1px solid rgba(var(--color-primary),0.15)' }}
        >
          <p className="text-callout font-semibold text-primary mb-xs">The Short Version</p>
          <p className="text-body text-text-secondary">
            We collect only what we need to provide you with a great experience. We never sell your data.
            You're in control of your information, and you can delete your account at any time.
          </p>
        </div>
      </div>

      {/* Quick nav */}
      <div
        className="p-lg rounded-large bg-surface mb-xxxl"
        style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
      >
        <p className="text-caption font-semibold text-text-tertiary uppercase tracking-wide mb-md">Contents</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-xs">
          {[
            '1. Information We Collect',
            '2. How We Use Your Information',
            '3. How We Share Your Information',
            '4. Your Rights and Choices',
            '5. Data Security',
            '6. Children\'s Privacy',
            '7. Data Retention',
            '8. International Data Transfers',
            '9. California Privacy Rights',
            '10. Changes to This Policy',
            '11. Third-Party Links',
            '12. Your Consent',
          ].map(item => (
            <p key={item} className="text-caption text-text-secondary hover:text-primary transition-colors cursor-pointer">
              {item}
            </p>
          ))}
        </div>
      </div>

      {/* Sections */}
      <Section number="1" title="Information We Collect">
        <SubSection title="Information You Provide">
          <p>When you use Nutrius, you may provide us with:</p>
          <BulletList items={[
            'Account Information: Email address, display name, and password (encrypted)',
            'Profile Information: Dietary preferences, restrictions, allergies, and health goals',
            'Family Profiles: Names and dietary information for family members you choose to track',
            'Content: Recipes, product reviews, discussions, and photos you share with the community',
            'Nutrition Data: Food logs, calorie tracking, and meal information',
            'Preferences: Favorite recipes, saved products, grocery lists, and collections',
          ]} />
        </SubSection>
        <SubSection title="Information We Collect Automatically">
          <BulletList items={[
            'Usage Data: How you interact with the app (features used, search queries, time spent)',
            'Device Information: Device type, operating system version, unique device identifiers',
            'Location Data: Approximate location for store recommendations (only if you grant permission)',
            'Crash Reports: Technical data to help us fix bugs and improve performance',
          ]} />
        </SubSection>
      </Section>

      <Section number="2" title="How We Use Your Information">
        <p>We use your information to:</p>
        <BulletList items={[
          'Provide Core Features: Enable nutrition tracking, recipe discovery, and community features',
          'Personalize Your Experience: Show relevant recipes and products based on your dietary needs',
          'Enable Community Features: Connect you with others and share community-created content',
          'Improve the App: Analyze usage patterns to enhance features and fix issues',
          'Communicate with You: Send important updates, respond to support requests, and notify you about relevant activity (if you opt in)',
          'Ensure Safety: Prevent fraud, abuse, and protect the community',
        ]} />
      </Section>

      <Section number="3" title="How We Share Your Information">
        <SubSection title="Public Information">
          <p>When you contribute to the Nutrius community, certain information is visible to other users:</p>
          <BulletList items={[
            'Recipes you publish (including ingredients, instructions, and photos)',
            'Product reviews and store location reports',
            'Your display name and profile information you choose to make public',
            'Comments and discussions you participate in',
          ]} />
        </SubSection>
        <SubSection title="Private Information">
          <p>The following information remains private and is never shared publicly:</p>
          <BulletList items={[
            'Your email address',
            'Your personal nutrition logs and calorie tracking data',
            'Family member profiles and their dietary information',
            'Your saved favorites, grocery lists, and private collections',
          ]} />
        </SubSection>
        <SubSection title="Service Providers">
          <p>We work with trusted third-party services to operate Nutrius:</p>
          <BulletList items={[
            'Firebase (Google): Database, authentication, and cloud storage',
            'Apple: App distribution, push notifications, and in-app purchases',
            'Analytics Services: To understand app usage and improve performance (anonymized data only)',
          ]} />
          <p className="mt-md">These service providers are contractually obligated to protect your data and use it only for providing services to Nutrius.</p>
        </SubSection>
        <SubSection title="Legal Requirements">
          <p>We may disclose your information if required by law, such as to comply with a court order, legal process, or government request.</p>
        </SubSection>
      </Section>

      <Section number="4" title="Your Rights and Choices">
        <div
          className="grid grid-cols-1 sm:grid-cols-2 gap-md mb-lg"
        >
          {[
            { icon: Eye,      title: 'View Your Data',      body: 'Access all information we have about you through your account settings.' },
            { icon: Lock,     title: 'Edit Your Info',      body: 'Update your profile, dietary preferences, and family profiles at any time.' },
            { icon: Trash2,   title: 'Delete Content',      body: 'Remove recipes, reviews, or other content you\'ve shared.' },
            { icon: Download, title: 'Download Your Data',  body: 'Request a copy of your information in a portable format.' },
            { icon: Bell,     title: 'Notifications',       body: 'Control what emails and push notifications you receive.' },
            { icon: MapPin,   title: 'Location Data',       body: 'Location access is fully optional and can be revoked at any time.' },
          ].map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className="p-md rounded-large bg-surface"
              style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
            >
              <div className="flex items-center gap-sm mb-sm">
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                  <Icon size={14} className="text-primary" />
                </div>
                <p className="text-caption font-semibold text-text-primary">{title}</p>
              </div>
              <p className="text-caption text-text-secondary">{body}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section number="5" title="Data Security">
        <p>We take security seriously and implement industry-standard measures to protect your information:</p>
        <BulletList items={[
          'Encryption: All data is encrypted in transit (HTTPS/TLS) and at rest',
          'Authentication: Secure password storage using industry-standard hashing',
          'Access Controls: Limited employee access to user data, with strict policies and monitoring',
          'Regular Audits: Ongoing security reviews and vulnerability testing',
        ]} />
        <p className="mt-md">
          While we implement strong security measures, no system is 100% secure. If you discover a security vulnerability, please report it to us immediately at{' '}
          <a href="mailto:security@nutrius.co" className="text-primary hover:underline">security@nutrius.co</a>.
        </p>
      </Section>

      <Section number="6" title="Children's Privacy">
        <p>
          Nutrius is not intended for children under 13. We do not knowingly collect personal information from children under 13.
          If you're a parent and believe your child has provided us with personal information, please contact us and we'll delete it promptly.
        </p>
        <div
          className="p-md rounded-medium mt-md"
          style={{ background: 'rgba(var(--color-accent),0.05)', border: '1px solid rgba(var(--color-accent),0.15)' }}
        >
          <p className="text-caption text-text-secondary">
            <strong className="text-text-primary">Note:</strong> Family profiles are designed for parents and caregivers to manage dietary information for their family members, not for children to create their own accounts.
          </p>
        </div>
      </Section>

      <Section number="7" title="Data Retention">
        <p>We retain your information for as long as your account is active or as needed to provide you services. When you delete your account:</p>
        <BulletList items={[
          'Your personal data and private information is deleted immediately',
          'Publicly shared content (recipes, reviews) may remain visible but will be anonymized',
          'Backup copies are deleted within 90 days',
          'Some data may be retained for legal compliance (e.g., transaction records for tax purposes)',
        ]} />
      </Section>

      <Section number="8" title="International Data Transfers">
        <p>
          Nutrius is operated in the United States, and your information may be transferred to and stored on servers in the U.S. or other countries where our service providers operate.
          By using Nutrius, you consent to the transfer of your information to countries outside your country of residence, which may have different data protection laws.
        </p>
        <p>
          For users in the European Economic Area (EEA), we comply with applicable data protection laws and use standard contractual clauses approved by the European Commission for data transfers.
        </p>
      </Section>

      <Section number="9" title="California Privacy Rights">
        <p>If you're a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):</p>
        <BulletList items={[
          'Right to Know: Request details about the personal information we\'ve collected about you',
          'Right to Delete: Request deletion of your personal information',
          'Right to Opt-Out: We don\'t sell your personal information, so there\'s nothing to opt out of',
          'Right to Non-Discrimination: We won\'t discriminate against you for exercising your privacy rights',
        ]} />
        <p className="mt-md">
          To exercise these rights, email us at{' '}
          <a href="mailto:privacy@nutrius.co" className="text-primary hover:underline">privacy@nutrius.co</a>.
        </p>
      </Section>

      <Section number="10" title="Changes to This Policy">
        <p>We may update this Privacy Policy from time to time. When we make significant changes, we'll:</p>
        <BulletList items={[
          'Update the "Last Updated" date at the top of this policy',
          'Notify you via email (if you\'ve provided one) or in-app notification',
          'Give you an opportunity to review the changes before they take effect',
        ]} />
        <p>Your continued use of Nutrius after changes take effect means you accept the updated policy.</p>
      </Section>

      <Section number="11" title="Third-Party Links">
        <p>
          Nutrius may contain links to third-party websites or services (e.g., product manufacturer websites, store locators).
          We're not responsible for the privacy practices of these third parties.
          We encourage you to read their privacy policies before providing any information.
        </p>
      </Section>

      <Section number="12" title="Your Consent">
        <p>
          By using Nutrius, you consent to the collection, use, and sharing of your information as described in this Privacy Policy.
          If you don't agree with this policy, please don't use the app.
        </p>
      </Section>

      {/* Footer CTA */}
      <div
        className="p-xl rounded-large text-center mt-xxxl"
        style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
      >
        <p className="text-callout text-text-secondary mb-md">Questions about your privacy?</p>
        <a
          href="mailto:privacy@nutrius.co"
          className="inline-flex items-center gap-sm px-xl py-md bg-primary text-white rounded-large text-callout font-semibold hover:bg-primary/90 transition-all"
        >
          Contact privacy@nutrius.co
        </a>
        <p className="text-caption text-text-tertiary mt-lg">
          <Link to="/" className="hover:text-primary transition-colors">Back to Nutrius</Link>
        </p>
      </div>

    </div>
  );
};

export default PrivacyPolicyView;
